import React, { useRef } from "react";
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  Modal,
  Backdrop,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import AppButton from "../../../../Common/AppButton";
import StageFooter from "../StageFooter";
import theme from "../../../../../utils/theme";
import useRFIAdditions from "../../../../../hooks/useRFIAdditions";
import { additionsColumn } from "../../../../../utils/constant";
import SkipNextIcon from "@mui/icons-material/SkipNext";

// Styles
const styles = {
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0 8px",
  },
  headerCell: {
    fontWeight: 600,
    padding: "12px 10px",
    borderBottom: "2px solid #e0e0e0",
    textAlign: "left",
    fontSize: "0.875rem",
    position: "relative",
    minWidth: "100px",
  },
  requiredIndicator: {
    color: "error.main",

    ml: 1,
    fontSize: "0.75rem",
  },
  cell: {
    padding: "8px",
    borderBottom: "1px solid #e0e0e0",
    position: "relative",
    verticalAlign: "top",
  },
  deleteButton: {
    color: "error.main",
    backgroundColor: "background.paper",
    border: "1px solid",
    borderColor: "grey.300",
    borderRadius: 1,
    width: 28,
    height: 28,
    boxShadow: 1,
    transition: "background 0.2s, color 0.2s",
    "&:hover": {
      backgroundColor: "error.light",
      color: "error.dark",
      borderColor: "error.main",
    },
  },
  addButton: {
    color: "info.main",
    backgroundColor: "background.paper",
    border: "1px solid",
    borderColor: "grey.300",
    borderRadius: 1,
    width: 28,
    height: 28,
    boxShadow: 1,
    transition: "background 0.2s, color 0.2s",
    "&:hover": {
      backgroundColor: "info.light",
      color: "info.dark",
      borderColor: "info.main",
    },
  },
  textField: {
    backgroundColor: "background.paper",
    "& .MuiInputBase-input": {
      height: 24,
      padding: "8px 12px",
    },
  },
  uploadButton: {
    width: "100%",
    height: "38px",
    textTransform: "none",
  },
  imagePreview: {
    width: 80,
    height: 80,
    objectFit: "cover",
    borderRadius: 1,
    border: "1px solid",
    borderColor: "grey.300",
  },
  addRowButton: {
    mt: 2,
    ml: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  mainContainer: {
    border: `2px solid ${theme.palette.grey[200]}`,
    py: 2,
    borderRadius: 2,
  },
  imagePreviewContainer: {
    position: "relative",
    width: 100,
    height: 100,
    border: "1px solid #ddd",
    borderRadius: 1,
    overflow: "hidden",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    "&:hover": {
      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
      transform: "scale(1.02)",
      transition: "all 0.2s ease-in-out",
    },
    cursor: "pointer",
  },
  closeButtonContainer: {
    position: "absolute",
    top: 4,
    right: 4,
    backgroundColor: "rgba(0,0,0,0.5)",
    borderRadius: "50%",
    width: 20,
    height: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 2,
  },
  modalContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalContent: {
    position: "relative",
    maxWidth: "90%",
    maxHeight: "90%",
    outline: "none",
    p: 1,
    borderRadius: 2,
    bgcolor: "background.paper",
    boxShadow: 24,
    transition: "all 300ms ease",
  },
  modalCloseButton: {
    position: "absolute",
    top: 8,
    right: 8,
    bgcolor: "rgba(0,0,0,0.5)",
    color: "white",
    "&:hover": {
      bgcolor: "rgba(0,0,0,0.7)",
    },
  },
  modalImage: {
    display: "block",
    maxWidth: "100%",
    maxHeight: "calc(90vh - 64px)",
    objectFit: "contain",
  },
  errorText: {
    color: "error.main",
    fontSize: "0.75rem",
    mt: 0.5,
  },
  fieldContainer: {
    position: "relative",
  },
  noDataContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "22vh",
    width: "100%",
    fontSize: "1rem",
    color: "text.secondary",
  },
  noDataText: {
    color: theme.palette.primary.main,
  },
};

const Additions = ({
  formEnabled = true,
  showAction = true,
  showSubmitButton = true,
  awaitingReview = false,
  showInspectorFields = true,
  stage,
  isAPICallAllowed,
  isProductEngineer,
  isAdmin,
  showAllColumns,
}) => {
  const {
    formik,
    previewOpen,
    previewImage,
    handleAddRow,
    handleDeleteRow,
    handleImageUpload,
    handleOpenPreview,
    handleClosePreview,
    skipStageFcn,
  } = useRFIAdditions({
    showInspectorFields,
    isAdmin,
    stageId: stage.stage_id,
    isAPICallAllowed,
  });

  const fileInputRefs = useRef([]);

  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    handleBlur,
    isSubmitting,
  } = formik;

  // Handle form submission with validation
  const handleSubmit = async (e) => {
    e.preventDefault();
    formik.handleSubmit(e);
  };

  const renderField = (index, column, row) => {
    const fieldName = `additions[${index}].${column.id}`;
    const fieldValue = values?.additions?.[index]?.[column.id];
    const fieldError = errors?.additions?.[index]?.[column.id];
    const fieldTouched = touched?.additions?.[index]?.[column.id];
    const showError = fieldTouched && fieldError;

    if (column.type === "image") {
      return (
        <Box sx={styles.fieldContainer}>
          {fieldValue?.preview || fieldValue ? (
            <Box sx={styles.imagePreviewContainer}>
              {formEnabled && !showInspectorFields && (
                <Box
                  sx={styles.closeButtonContainer}
                  onClick={() => {
                    const updatedAdditions = [...values?.additions];
                    updatedAdditions[index] = {
                      ...updatedAdditions[index],
                      picture: null,
                    };
                    setFieldValue("additions", updatedAdditions);
                  }}
                >
                  <CloseIcon sx={{ color: "#fff", fontSize: 16 }} />
                </Box>
              )}
              <Box
                component="img"
                src={fieldValue?.preview || fieldValue}
                alt="Part"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onClick={() => handleOpenPreview(index, values)}
              />
            </Box>
          ) : (
            <>
              <input
                ref={(el) => (fileInputRefs.current[index] = el)}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) =>
                  handleImageUpload(index, e, values, setFieldValue)
                }
              />
              <AppButton
                variant="contained"
                buttonType="main"
                component="label"
                label="Upload"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRefs.current[index]?.click()}
                disabled={!formEnabled}
              />
            </>
          )}
          {showError && <Box sx={styles.errorText}>{fieldError}</Box>}
        </Box>
      );
    } else if (column.type === "checkbox") {
      return (
        <Box sx={styles.fieldContainer}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!fieldValue}
                onChange={handleChange}
                onBlur={handleBlur}
                name={fieldName}
                disabled={!formEnabled}
                size="small"
                color="primary"
              />
            }
            label=""
            sx={{ margin: 0, justifyContent: "center" }}
          />
          {showError && <Box sx={styles.errorText}>{fieldError}</Box>}
        </Box>
      );
    } else if (column.type === "actions") {
      return (
        formEnabled &&
        !showInspectorFields && (
          <Tooltip title="Delete row" placement="top" arrow>
            <IconButton
              size="small"
              onClick={() => handleDeleteRow(index, values, setFieldValue, row)}
              // disabled={values.additions.length === 1}
              sx={styles.deleteButton}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      );
    } else {
      return (
        <Box sx={styles.fieldContainer}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={fieldValue || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            name={fieldName}
            disabled={
              !formEnabled || (showInspectorFields && column.id !== "remarks")
            }
            type={column.type}
            error={showError}
            inputProps={{
              inputMode: column.type === "number" ? "numeric" : "text",
              pattern: column.type === "number" ? "[0-9]*" : undefined,
              style: { padding: "6px 10px" },
            }}
          />
          {showError && <Box sx={styles.errorText}>{fieldError}</Box>}
        </Box>
      );
    }
  };

  const filteredColumns = additionsColumn.filter(
    (column) => !column.showCondition || column.showCondition(showAllColumns)
  );

  if (stage.is_skipped) {
    return (
      <Box sx={{ ...styles.noDataContainer, minHeight: "46vh" }}>
        <Typography variant="h6" sx={styles.noDataText}>
          This Work Order does not require any Add-ons.
        </Typography>
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={styles.mainContainer}>
        <Box sx={{ overflowX: "auto" }}>
          {values?.additions?.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  {filteredColumns.map((column) => (
                    <th
                      key={column.id}
                      style={{ ...styles.headerCell, minWidth: column.width }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {column.label}
                        {column.required && formEnabled && (
                          <Typography sx={styles.requiredIndicator}>
                            *
                          </Typography>
                        )}
                      </Box>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {values?.additions?.map((row, index) => (
                  <tr key={index}>
                    {filteredColumns.map((column) => (
                      <td key={column.id} style={{ ...styles.cell }}>
                        {renderField(index, column, row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <>
              {!isProductEngineer ? (
                <Box sx={styles.noDataContainer}>
                  <Typography variant="h6" sx={styles.noDataText}>
                    Waiting for Project Engineer to create Add-on list
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{ ...styles.noDataContainer, minHeight: "0vh", mt: 3 }}
                >
                  <Typography variant="body1" sx={styles.noDataText}>
                    <b>Add Add-ons</b> or <b>Skip</b>
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {formEnabled && isProductEngineer && (
          <Box
            sx={{
              ...styles.addRowButton,
              ...(!(values?.additions?.length > 0) && { py: 3, pb: 5 }),
            }}
            gap={2}
          >
            {!(values?.additions?.length > 0) && (
              <AppButton
                variant="contained"
                buttonType="primary"
                component="label"
                label="Skip"
                startIcon={<SkipNextIcon />}
                onClick={() => skipStageFcn()}
                disabled={!formEnabled}
              />
            )}
            <Tooltip title="Add row" placement="top" arrow>
              {!(values?.additions?.length > 0) ? (
                <AppButton
                  variant="contained"
                  buttonType="main"
                  component="label"
                  label="Add Add-ons"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddRow(values, setFieldValue)}
                  disabled={!formEnabled}
                />
              ) : (
                <IconButton
                  size="small"
                  onClick={() => handleAddRow(values, setFieldValue)}
                  sx={styles.addButton}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              )}
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Image Preview Modal */}
      <Modal
        open={previewOpen}
        onClose={handleClosePreview}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={styles.modalContainer}
      >
        <Box sx={styles.modalContent}>
          <IconButton sx={styles.modalCloseButton} onClick={handleClosePreview}>
            <CloseIcon />
          </IconButton>
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt="Preview"
              sx={styles.modalImage}
            />
          )}
        </Box>
      </Modal>

      <StageFooter
        showAction={showAction && values?.additions?.length > 0}
        showSubmitButton={showSubmitButton}
        onSendForApproval={handleSubmit}
        isSubmitting={isSubmitting}
        awaitingReview={awaitingReview}
        stage={stage}
        submitButtonText={
          isProductEngineer
            ? "Submit Additions"
            : "Send for approval to QC Coordinator"
        }
      />
    </form>
  );
};

export default Additions;
